import React from 'react';
import { getMotionSafeTransition } from '../../utils/motionUtils';

interface CarouselArrowProps {
  direction: 'left' | 'right';
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  testId?: string;
}

/**
 * CarouselArrow - Navigation arrow component for carousels
 * 
 * Features:
 * - Left and right arrow variants
 * - Hover, active, and disabled states
 * - Accessible with proper ARIA labels
 * - Matches site's dark theme aesthetic
 * - Smooth transitions and animations
 * - Enhanced visual feedback
 */
const CarouselArrow: React.FC<CarouselArrowProps> = ({
  direction,
  onClick,
  disabled = false,
  className = '',
  testId = `carousel-arrow-${direction}`
}) => {
  const isLeft = direction === 'left';
  
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        w-8 h-8 sm:w-9 sm:h-9 
        bg-gray-900/70 hover:bg-gray-800/85 
        border border-gray-600/20 hover:border-gray-500/40
        rounded-full 
        flex items-center justify-center
        text-gray-400 hover:text-white
        transition-all duration-300 cubic-bezier(0.34, 1.56, 0.64, 1)
        shadow-md hover:shadow-lg hover:shadow-red-500/10
        backdrop-blur-sm
        disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:bg-gray-900/70
        disabled:hover:text-gray-400 disabled:hover:border-gray-600/20
        focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:ring-offset-2 focus:ring-offset-transparent
        transform hover:scale-110 active:scale-95
        ${disabled ? 'pointer-events-none' : 'cursor-pointer'}
        ${className}
      `}
      style={{
        // Enhanced motion-safe transitions
        transition: getMotionSafeTransition(
          'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          'background-color 0.2s ease-out, border-color 0.2s ease-out, color 0.2s ease-out'
        )
      }}
      aria-label={`Scroll ${direction}`}
      aria-disabled={disabled}
      data-testid={testId}
    >
      <svg
        className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 cubic-bezier(0.34, 1.56, 0.64, 1) ${
          disabled ? 'opacity-50' : ''
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{
          // Enhanced arrow animation with subtle bounce and motion-safe transitions
          filter: disabled ? 'none' : 'drop-shadow(0 0 2px rgba(239, 68, 68, 0.1))',
          transition: getMotionSafeTransition(
            'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            'opacity 0.2s ease-out'
          )
        }}
      >
        {isLeft ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M15 19l-7-7 7-7"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M9 5l7 7-7 7"
          />
        )}
      </svg>
    </button>
  );
};

export default CarouselArrow;
